import React from "react";
import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
// import ReactGA from "react-ga4";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import { useNavigate, useLocation } from "react-router-dom";
import HappyFace from "../../HappyFace.png";
import SadFace from "../../SadFace.png";
import useGAEventsTracker from "../../GaEventTracker";
import CloseIcon from "@mui/icons-material/Close";

import { emailDispatch } from "../utils";
const DiabetesRiskForm = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const {
    clientId,
    labNo,
    patient_age,
    patient_gender,
    systolic,
    systolic_unit,
    diastolic,
    diastolic_unit,
    pulse,
    bmi,
    urban,
    riskType,
    hba1c,
    hba1c_unit,
    fbs,
    fbs_unit,
    waistCircumference,
    parents_with_diabetes,
    physicalActivity,
  } = Object.fromEntries(queryParams);
  const [age, setAge] = useState(patient_age ? patient_age : "");
  const [gender, setGender] = useState(
    patient_gender ? patient_gender : "male"
  );
  const [systolicValue, setSystolicValue] = useState(systolic || "");
  const [diastolicValue, setDiastolicValue] = useState(diastolic || "");
  const [systolicUnit, setSystolicUnit] = useState(systolic_unit || "mm Hg");
  const [diastolicUnit, setDiastolicUnit] = useState(diastolic_unit || "mm Hg");
  const [pulseValue, setPulseValue] = useState(pulse || "");
  const [bmiValue, setBmiValue] = useState(bmi || "");
  const [urbanStatus, setUrbanStatus] = useState(
    urban ? JSON.parse(urban) : false
  );

  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [hba1cValue, setHba1cValue] = useState(hba1c || "");
  const [hba1cUnit, setHba1cUnit] = useState(hba1c_unit || "%");
  const [fbsValue, setFbsValue] = useState(fbs || "");
  const [fbsUnit, setFbsUnit] = useState(fbs_unit || "mg/dL");
  const [waistCircumferenceValue, setWaistCircumferenceValue] = useState(
    waistCircumference || ""
  );
  const [familyHistoryOfDiabetes, setFamilyHistoryOfDiabetes] = useState(
    parents_with_diabetes || "none"
  );
  const [physicalActivityType, setPhysicalActivityType] = useState(
    physicalActivity || "sedentary"
  );
  const [checked, setChecked] = useState(false);
  const [checkBoxError, setCheckBoxError] = useState(false);
  const [open, setOpen] = useState(false);

  const [riskScore, setRiskScore] = useState("");
  const [loader, setLoader] = useState(false);

  const navigate = useNavigate();
  const GAEventsTracker = useGAEventsTracker("risk score form page");

  const questionDetails = {
    urban: {
      question: "Do you belong to an urban area?",
      options: [
        { title: "Yes", value: true },
        { title: "No", value: false },
      ],
    },
    familyHistory: {
      question: "How many of your parents have been diagnosed with diabetes?",
      options: [
        { title: "One", value: "one" },
        { title: "Two", value: "two" },
        { title: "None", value: "none" },
      ],
    },
    physical: {
      question:
        "How would you describe your typical level of physical activity?",
      options: [
        { title: "Sedentary", value: "sedentary" },
        { title: "Moderate", value: "moderate" },
        { title: "Regular", value: "regular" },
      ],
    },
  };

  function getNumericValue(value) {
    let result = "";
    if (value && !isNaN(parseFloat(value))) {
      result = parseFloat(value);
    }

    return result;
  }

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const calculateBMI = (e) => {
    e.preventDefault();
    let heightInMeters = height / 100; // Convert cm to meters
    let bmi = weight / (heightInMeters * heightInMeters);
    if (bmi) {
      bmi = bmi.toFixed(2);
      setBmiValue(bmi);
    } else {
      setBmiValue("");
    }
  };

  const dialogBox = () => {
    return (
      <Dialog open={open} onClose={handleClose} fullWidth>
        <DialogTitle>
          <Typography variant="h6">BMI Calculator</Typography>
        </DialogTitle>
        <DialogContent>
          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>

          <form onSubmit={calculateBMI}>
            <Stack direction={"row"} alignItems={"center"} gap={3} pt={1}>
              <TextField
                fullWidth
                size="small"
                label="Height (cm)"
                placeholder="Height in cm"
                variant="outlined"
                onChange={(e) => setHeight(e.target.value)}
                value={height}
                required
              />
              <TextField
                fullWidth
                size="small"
                label="Weight (kg)"
                placeholder="Weight in kg"
                variant="outlined"
                onChange={(e) => setWeight(e.target.value)}
                value={weight}
                required
              />
            </Stack>
            <Box mt={2}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                type="submit"
              >
                Calculate BMI
              </Button>
              {bmiValue ? <Typography mt={1}>BMI: {bmiValue}</Typography> : ""}
            </Box>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!checked) {
      setCheckBoxError("Please check this box");
      return;
    }

    if (checked) {
      setCheckBoxError("");
    }
    setLoader(true);
    const url = `https://6ohh9xcizb.execute-api.ap-south-1.amazonaws.com/prod/?riskType=diabetes`;

    const payload = {
      clientId: clientId,
      labNo: labNo,
      age: getNumericValue(age),
      gender: gender,
      results: [
        {
          name: "Systolic",
          value: getNumericValue(systolicValue),
          unit: systolicUnit,
        },
        {
          name: "Diastolic",
          value: getNumericValue(diastolicValue),
          unit: diastolicUnit,
        },
        {
          name: "Body Mass Index(BMI)",
          value: getNumericValue(bmiValue),
          unit: "",
        },
        {
          name: "Pulse",
          value: getNumericValue(pulseValue),
          unit: "",
        },
        {
          name: "Hba1c",
          value: getNumericValue(hba1cValue),
          unit: hba1c_unit,
        },
        {
          name: "Fbs",
          value: getNumericValue(fbsValue),
          unit: fbsUnit,
        },
      ],
      lifestyle: {
        isUrban: urbanStatus,
        physicalActivity: physicalActivityType,
      },
      measurements: {
        waistCircumference: getNumericValue(waistCircumferenceValue),
      },
      isPartialRiskScore: false,
    };

    let userFilledData = {
      labNo,
      systolicValue,
      diastolicValue,
      pulseValue,
      bmiValue,
      urbanStatus,
      physicalActivity,
      waistCircumference,
    };

    const options = {
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify(payload),
    };

    try {
      const response = await fetch(url, options);
      const data = await response.json();

      if (data.isSuccess && data?.data?.risk) {
        GAEventsTracker("submitted", "successfully", "diabetes risk");
        GAEventsTracker("risk score data", data.data.risk, "diabetes risk");
        setRiskScore(data.data.risk);
        await emailDispatch(userFilledData, riskType, clientId);
        Swal.fire({
          iconHtml: `<img src=${HappyFace}>`,
          customClass: {
            icon: "no-border",
          },
          width: "400px",
          title: "Success!",
          text: "Your risk score has been generated successfully.",
        });
        navigate("/riskScore", {
          state: {
            riskData: data.data.risk,
            riskType: "diabetes",
            clientId: clientId,
          },
        });
      } else {
        GAEventsTracker("submission", "unsuccessful");
        Swal.fire({
          iconHtml: `<img src=${SadFace}>`,
          customClass: {
            icon: "no-border",
          },
          width: "400px",
          title: "Error",
          text: "Something went wrong. Pleasse try again.",
        });
      }
    } catch (error) {
      console.log("Risk score error", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error,
      });
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (checked) {
      setCheckBoxError("");
    }
  }, [checked]);

  return (
    <Stack
      sx={{ padding: { md: "30px", sm: "20px", xs: "0px" } }}
      direction="row"
      justifyContent={"center"}
    >
      <Box
        p={4}
        sx={{
          width: { md: "80%", sm: "100%", xs: "100%" },
          background: "#EDF9FE",
          borderRadius: "16px",
        }}
      >
        <Typography variant="h5">Basic Information</Typography>
        <form onSubmit={handleSubmit}>
          <Stack direction={"column"} gap={2.5} mt={3}>
            <Stack
              gap={3}
              sx={{
                flexDirection: { md: "row", sm: "column", xs: "column" },
                alignItems: {
                  md: "center",
                  sm: "flex-start",
                  xs: "flex-start",
                },
              }}
            >
              <Box>
                <TextField
                  fullWidth
                  size={"small"}
                  label="Age"
                  placeholder="age should be between 20-75"
                  variant="outlined"
                  onChange={(e) => setAge(e.target.value)}
                  value={age}
                  required
                  maxWidth="sm"
                />
              </Box>
              <Box>
                <FormControl
                  onChange={(e) => setGender(e.target.value)}
                  value={gender}
                  sx={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <FormLabel sx={{ marginRight: "16px", width: "70px" }}>
                    Gender
                  </FormLabel>
                  <RadioGroup row defaultValue={gender}>
                    <FormControlLabel
                      value="male"
                      control={<Radio sx={{ padding: "6px" }} size="small" />}
                      label="Male"
                    />
                    <FormControlLabel
                      value="female"
                      control={<Radio sx={{ padding: "6px" }} size="small" />}
                      label="Female"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            </Stack>
            <Stack
              gap={3}
              sx={{
                flexDirection: { md: "row", sm: "column", xs: "column" },
                alignItems: {
                  md: "center",
                  sm: "flex-start",
                  xs: "flex-start",
                },
              }}
            >
              <Box flexGrow={1}>
                <Typography>Systolic BP</Typography>
                <Stack direction={"row"} alignItems={"center"} gap={0.5} mt={1}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Value"
                    placeholder="Enter Value"
                    variant="outlined"
                    onChange={(e) => setSystolicValue(e.target.value)}
                    value={systolicValue}
                    required
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Unit"
                    placeholder="Enter Unit"
                    variant="outlined"
                    onChange={(e) => setSystolicUnit(e.target.value)}
                    value={systolicUnit}
                    required
                  />
                </Stack>
              </Box>
              <Box flexGrow={1}>
                <Typography>Diastolic BP</Typography>
                <Stack direction={"row"} alignItems={"center"} gap={0.5} mt={1}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Value"
                    placeholder="Enter Value"
                    variant="outlined"
                    onChange={(e) => setDiastolicValue(e.target.value)}
                    value={diastolicValue}
                    required
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Unit"
                    placeholder="Enter Unit"
                    variant="outlined"
                    onChange={(e) => setDiastolicUnit(e.target.value)}
                    value={diastolicUnit}
                    required
                  />
                </Stack>
              </Box>
            </Stack>
            <Stack
              gap={3}
              sx={{
                flexDirection: { md: "row", sm: "column", xs: "column" },
                alignItems: {
                  md: "center",
                  sm: "flex-start",
                  xs: "flex-start",
                },
              }}
            >
              <Stack direction={"column"} gap={2} flexGrow={1}>
                <Box sx={{ width: "100%" }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Pulse"
                    placeholder="Enter your pulse value"
                    variant="outlined"
                    onChange={(e) => setPulseValue(e.target.value)}
                    value={pulseValue}
                    required
                    // sx={{ width: { md: "80%", sm: "100%", xs: "100%" } }}
                  />
                </Box>
              </Stack>

              <Box>
                <TextField
                  fullWidth
                  size="small"
                  label="Waist Circumference"
                  placeholder="Enter value"
                  variant="outlined"
                  onChange={(e) => setWaistCircumferenceValue(e.target.value)}
                  value={waistCircumferenceValue}
                  required
                  // sx={{ width: { md: "80%", sm: "100%", xs: "100%" } }}
                />
              </Box>

              <Stack direction={"column"} gap={2} flexGrow={1}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    width: "100%",
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    label="BMI"
                    placeholder="BMI"
                    variant="outlined"
                    onChange={(e) => setBmiValue(e.target.value)}
                    value={bmiValue}
                    required
                    sx={{ width: { md: "40%", sm: "50%", xs: "50%" } }}
                  />
                  <Typography
                    onClick={handleClickOpen}
                    sx={{
                      textDecoration: "underline",
                      cursor: "pointer",
                      color: "gray",
                    }}
                  >
                    Calculate BMI
                  </Typography>
                </Box>

                {dialogBox()}
              </Stack>
            </Stack>

            <Stack
              gap={3}
              sx={{
                flexDirection: { md: "row", sm: "column", xs: "column" },
                alignItems: {
                  md: "center",
                  sm: "flex-start",
                  xs: "flex-start",
                },
              }}
            >
              <Box flexGrow={1}>
                <Typography>Hba1c</Typography>
                <Stack direction={"row"} alignItems={"center"} gap={0.5} mt={1}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Value"
                    placeholder="Enter Value"
                    variant="outlined"
                    onChange={(e) => setHba1cValue(e.target.value)}
                    value={hba1cValue}
                    required
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Unit"
                    placeholder="Enter Unit"
                    variant="outlined"
                    onChange={(e) => setHba1cUnit(e.target.value)}
                    value={hba1cUnit}
                    required
                  />
                </Stack>
              </Box>
              <Box flexGrow={1}>
                <Typography>Fasting Blood Sugar</Typography>
                <Stack direction={"row"} alignItems={"center"} gap={0.5} mt={1}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Value"
                    placeholder="Enter Value"
                    variant="outlined"
                    onChange={(e) => setFbsValue(e.target.value)}
                    value={fbsValue}
                    required
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Unit"
                    placeholder="Enter Unit"
                    variant="outlined"
                    onChange={(e) => setFbsUnit(e.target.value)}
                    value={fbsUnit}
                    required
                  />
                </Stack>
              </Box>
            </Stack>

            <Box>
              <FormControl required>
                <FormLabel
                  id="demo-radio-buttons-group-label"
                  sx={{ color: "#3567D6" }}
                >
                  {questionDetails.urban.question}
                </FormLabel>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  value={urbanStatus}
                  name="radio-buttons-group"
                  onChange={(e) => setUrbanStatus(e.target.value === "true")}
                  row
                >
                  {questionDetails.urban.options.map((each) => (
                    <FormControlLabel
                      key={each.title}
                      value={each.value}
                      control={<Radio size="small" />}
                      label={each.title}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>

            <Box>
              <FormControl required>
                <FormLabel
                  id="demo-radio-buttons-group-label"
                  sx={{ color: "#3567D6" }}
                >
                  {questionDetails.familyHistory.question}
                </FormLabel>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  value={familyHistoryOfDiabetes}
                  name="radio-buttons-group"
                  onChange={(e) => setFamilyHistoryOfDiabetes(e.target.value)}
                  row
                >
                  {questionDetails.familyHistory.options.map((each) => (
                    <FormControlLabel
                      key={each.title}
                      value={each.value}
                      control={<Radio size="small" />}
                      label={each.title}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>

            <Box>
              <FormControl required>
                <FormLabel
                  id="demo-radio-buttons-group-label"
                  sx={{ color: "#3567D6" }}
                >
                  {questionDetails.physical.question}
                </FormLabel>
                <RadioGroup
                  aria-labelledby="demo-radio-buttons-group-label"
                  value={physicalActivityType}
                  name="radio-buttons-group"
                  onChange={(e) => setPhysicalActivityType(e.target.value)}
                  row
                >
                  {questionDetails.physical.options.map((each) => (
                    <FormControlLabel
                      key={each.title}
                      value={each.value}
                      control={<Radio size="small" />}
                      label={each.title}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>

            <Box>
              <Stack direction="row" alignItems="center">
                <Checkbox
                  onChange={() => setChecked(!checked)}
                  checked={checked}
                />
                <Typography variant="p">
                  I acknowledge & agree that I’m sharing metabolic data for
                  accurate results.
                </Typography>
              </Stack>
              {checkBoxError && (
                <Typography
                  pl={5}
                  variant="p"
                  sx={{ color: "red", fontSize: "12px" }}
                >
                  {checkBoxError}
                </Typography>
              )}
            </Box>
            <Box>
              <Button
                type="submit"
                variant="contained"
                color="success"
                sx={{ width: { md: "40%", sm: "100%", xs: "100%" } }}
              >
                {loader ? (
                  <CircularProgress sx={{ color: "#fff" }} size="30px" />
                ) : (
                  "Get Diabetes Risk Score"
                )}
              </Button>
            </Box>
          </Stack>
        </form>
      </Box>
    </Stack>
  );
};

export default DiabetesRiskForm;
